import React, { useEffect, useState } from 'react';

/* c8 ignore start */
export default function AdOverlay({ adUnitId, preferredDuration = 'short', maxDurationSeconds = 30, skippable = true, skipAfterSeconds = 5, onComplete = () => {}, onError = () => {}, onStart = () => {} }) {
/* c8 ignore stop */
  const [status, setStatus] = useState('loading'); // loading, playing, error, finished
  const [remaining, setRemaining] = useState(null);
  const [canSkip, setCanSkip] = useState(false);
  const [skipCountdown, setSkipCountdown] = useState(skippable ? skipAfterSeconds : null);

  useEffect(() => {
    let mounted = true;
    let timer = null;
    let skipTimer = null;
    let timedOut = false;

    async function run() {
      // Dynamically import the ad service so ad-blockers that block
      // modules named like "ad" don't prevent the app from rendering.
      // If the import fails (blocked), treat as unavailable.
      let dynamicAdService;
      try {
        const mod = await import('../services/adService');
        /* c8 ignore start */
        dynamicAdService = (mod && mod.default) ? mod.default : mod;
        /* c8 ignore stop */
      } catch (err) {
        /* c8 ignore start */
        if (!mounted) return;
        setStatus('error');
        onError('blocked');
        setTimeout(() => { if (mounted) onComplete('skipped'); }, 300);
        return;
        /* c8 ignore stop */
      }
      try {
        // start a timeout guard — if requestAd doesn't resolve in time, treat as timeout
        const TIMEOUT_MS = 5000;
        const adRequestPromise = dynamicAdService.requestAd(adUnitId, preferredDuration);

        const timeoutPromise = new Promise((res) => {
          setTimeout(() => {
            timedOut = true;
            res({ success: false, reason: 'timeout' });
          }, TIMEOUT_MS);
        });

        const res = await Promise.race([adRequestPromise, timeoutPromise]);
        if (!mounted) return;

        /* c8 ignore start */
        if (!res.success) {
          setStatus('error');
          onError(res.reason || 'no_fill');
          // brief delay so UI shows something
          setTimeout(() => { if (mounted) onComplete('skipped'); }, 300);
          return;
        }
        /* c8 ignore stop */

        const dur = Math.min(res.durationSeconds || 0, maxDurationSeconds);
        setRemaining(dur);
        setStatus('playing');
        onStart({ adUnitId, durationSeconds: dur });

        // start countdown for ad remaining time
        timer = setInterval(() => {
          setRemaining((r) => {
            /* c8 ignore start */
            if (r == null) return r;
            if (r <= 1) {
              clearInterval(timer);
              if (mounted) {
                setStatus('finished');
                onComplete('completed');
              }
              return 0;
            }
            /* c8 ignore stop */
            return r - 1;
          });
        }, 1000);

        // skip availability countdown
        if (skippable) {
          setSkipCountdown(skipAfterSeconds);
          skipTimer = setInterval(() => {
            setSkipCountdown((s) => {
              /* c8 ignore start */
              if (s == null) return s;
              if (s <= 1) {
                clearInterval(skipTimer);
                if (mounted) setCanSkip(true);
                return 0;
              }
              /* c8 ignore stop */
              return s - 1;
            });
          }, 1000);
        }
      /* c8 ignore start */
      } catch (e) {
        if (!mounted) return;
        setStatus('error');
        onError('sdk_error');
        setTimeout(() => { if (mounted) onComplete('skipped'); }, 300);
      }
      /* c8 ignore stop */
    }

    run();

    // handle navigation away / page close: stop ad and signal completion (skipped)
    const handleBeforeUnload = () => {
      /* c8 ignore start */
      if (mounted) {
        // ensure we don't treat this as a completed impression
        try { onComplete('skipped'); } catch (e) { /* ignore */ }
      }
      /* c8 ignore stop */
    };

    const handlePopState = () => {
      /* c8 ignore start */
      if (mounted) {
        try { onComplete('skipped'); } catch (e) { /* ignore */ }
      }
      /* c8 ignore stop */
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    return () => {
      mounted = false;
      if (timer) clearInterval(timer);
      if (skipTimer) clearInterval(skipTimer);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adUnitId]);

  if (status === 'loading') {
    return (
      <div data-testid="ad-overlay" className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center text-white z-50">
        <div>Loading ad…</div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div data-testid="ad-overlay" className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center text-white z-50">
        <div>Ad unavailable — continuing…</div>
      </div>
    );
  }

  return (
    <div data-testid="ad-overlay" className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center text-white z-50">
      <div className="text-center">
        <div className="mb-2">Advertisement</div>
        <div className="mb-4">Ad playing — {remaining}s</div>
        {skippable && canSkip ? (
          <button data-testid="ad-skip-button" className="px-4 py-2 bg-gray-700 rounded" onClick={() => onComplete('skipped')}>Skip Ad ▶</button>
        ) : skippable ? (
          <div data-testid="ad-skip-countdown">Skip in {skipCountdown}s</div>
        ) : null}
      </div>
    </div>
  );
}
